import { Button, IconButton } from "@ai-teach/ui-web";
import { useRef, useState } from "react";
import { FiUpload, FiX } from "react-icons/fi";
import { HiVolumeUp } from "react-icons/hi";
import { MdAutoAwesome } from "react-icons/md";

interface AudioUploadProps {
  onFileSelect: (file: File | null) => void;
  currentAudioUrl?: string | null;
  onRemove?: () => void;
  /** When provided, shows a "Generate (TTS)" button; the returned audio becomes the selected file. */
  onTtsGenerate?: () => Promise<File | null>;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp3"];

export function AudioUpload({
  onFileSelect,
  onRemove,
  currentAudioUrl,
  onTtsGenerate,
}: AudioUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentAudioUrl ?? null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = (file: File) => {
    setError(null);

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Invalid file type. Allowed types: MP3, WAV, OGG");
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleRemove = () => {
    if (previewUrl && selectedFile) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
    setIsPlaying(false);
    onFileSelect(null);
    onRemove?.();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleTtsGenerate = async () => {
    if (!onTtsGenerate || isGenerating) return;
    setIsGenerating(true);
    try {
      const file = await onTtsGenerate();
      if (file) handleFile(file);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div className="space-y-2">
      {/* Hidden audio element */}
      {previewUrl && (
        <audio ref={audioRef} src={previewUrl} onEnded={handleAudioEnded} preload="metadata">
          <track kind="captions" label={previewUrl} />
        </audio>
      )}

      {/* Preview or Upload Zone */}
      {previewUrl ? (
        <div className="relative flex items-center gap-3 p-4 bg-surface border border-gray-800 rounded-lg">
          {/* Play Button */}
          <button
            type="button"
            onClick={handlePlayPause}
            className={`p-3 rounded-full transition-colors ${
              isPlaying
                ? "bg-primary-500 text-white"
                : "bg-primary-500/10 text-primary-500 hover:bg-primary-500/20"
            }`}
            aria-label={isPlaying ? "Stop" : "Play"}
          >
            <HiVolumeUp className="w-6 h-6" />
          </button>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-secondary truncate">
              {selectedFile?.name || "Audio file"}
            </p>
            <p className="text-xs text-muted">
              {selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : "Uploaded"}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <IconButton variant="ghost" size="sm" onClick={handleClick} aria-label="Change audio">
              <FiUpload className="h-4 w-4" />
            </IconButton>
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              aria-label="Remove audio"
              className="text-error-500 hover:bg-error-500/10"
            >
              <FiX className="h-4 w-4" />
            </IconButton>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileInput}
            className="hidden"
            aria-label="Upload audio"
          />
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`${
            isDragging
              ? "border-primary-500 bg-primary-500/10"
              : "border-gray-800 bg-surface hover:border-gray-700"
          } cursor-pointer h-24 rounded-lg border-2 border-dashed p-4 transition-colors flex items-center justify-center`}
        >
          <div className="text-center">
            <HiVolumeUp className="mx-auto h-8 w-8 text-secondary-200" aria-hidden="true" />
            <p className="mt-1 text-sm font-medium text-secondary-200">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-muted">MP3, WAV, OGG (Max 10MB)</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileInput}
            className="hidden"
            aria-label="Upload audio"
          />
        </div>
      )}

      {/* TTS Generate */}
      {onTtsGenerate && (
        <Button
          variant="secondary"
          onClick={handleTtsGenerate}
          disabled={isGenerating}
          className="w-full"
        >
          <MdAutoAwesome className="h-5 w-5" />
          {isGenerating ? "Generating..." : "Generate (TTS)"}
        </Button>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-error-500/10 border border-error-500/20 p-3">
          <p className="text-sm text-error-500">{error}</p>
        </div>
      )}
    </div>
  );
}
